import React, { useEffect } from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import { ActivityIndicator, View } from 'react-native'
import { AuthService, Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import { Controller, useForm } from 'react-hook-form'
import { ZIO, Throwable } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import PDTable from './components/PDTable'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { PolicyDispatchingService, RdoItem } from './services/PolicyDispatchingService'
import { PolicyDispatchingStackParamList } from './PolicyDispatchingStackParamList'
import { borderedInputStyle } from './styles/policyDispatchingStyles'
import { getPermissions } from './utils/policyDispatchingUtils'

type FormValues = {
  rdoCode: string
  fullName: string
}

type RdoRow = RdoItem & { statusLabel: string; checked?: boolean }

type PageInfo = {
  page: number
  totalPages: number
  totalElements: number
  first: boolean
  last: boolean
}

const headCell = [
  { id: 1, label: 'Mã RDO' },
  { id: 2, label: 'Họ và tên' },
  { id: 3, label: 'Văn phòng' },
  { id: 4, label: 'Trạng thái' },
  { id: 5, label: 'Xem CK' }
]

const rowCell = [
  { id: 1, label: 'rdoCode' },
  { id: 2, label: 'fullName' },
  { id: 3, label: 'office' },
  { id: 4, label: 'statusLabel' },
  { id: 5, label: 'signatureUrl' }
]

const toRow = (item: RdoItem, checked: boolean): RdoRow => ({
  ...item,
  statusLabel: item.inactive ? 'Ngưng dùng' : 'Đang dùng',
  checked
})

const hasRdoBody = (response: unknown): response is { body: { items: RdoItem[]; page: PageInfo & { size: number } } } =>
  Boolean(response) && typeof response === 'object' && 'body' in (response as object)

const unwrapZioEither = (response: unknown): unknown =>
  response && typeof response === 'object' && '_tag' in response
    ? (response as any)._tag === 'Right'
      ? (response as any).right
      : null
    : response

type Props = StackScreenProps<PolicyDispatchingStackParamList, 'PDRDOManagementScreen'>

export default function PDRDOManagementScreen({ navigation, route }: Props) {
  const isRequireSearch = route.params?.isRequireSearch ?? false
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { rdoCode: '', fullName: '' }
  })
  const [rows, setRows] = React.useState<RdoRow[]>([])
  const [selectedRows, setSelectedRows] = React.useState<RdoRow[]>([])
  const [allRowsSelected, setAllRowsSelected] = React.useState(false)
  const [pageInfo, setPageInfo] = React.useState<PageInfo | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const lastSearchValues = React.useRef<FormValues | null>(null)
  const permissions = getPermissions()
  const allowCreate = permissions.includes(PolicyDispatchingPermission.RDOCreate)
  const allowDeactivate = permissions.includes(PolicyDispatchingPermission.RDODeactivate) && selectedRows.length > 0

  const search = (values: FormValues, page: number, resetSelection = false) => {
    if (!values.rdoCode?.trim() && !values.fullName?.trim()) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập ít nhất một trong các thông tin để tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchRdo({
        rdoCode: values.rdoCode?.trim() || undefined,
        fullName: values.fullName?.trim() || undefined,
        nonFileNet: 'false',
        page
      }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!hasRdoBody(response)) {
            setRows([])
            setSelectedRows([])
            setAllRowsSelected(false)
            setPageInfo(null)
            setIsLoading(false)
            setModalInfo({ title: 'Tìm kiếm', message: 'Không tìm được thông tin theo yêu cầu.' })
            setOpenModal(true)
            return
          }

          const preserveAllSelected = !resetSelection && allRowsSelected
          const selectedKeys = new Set((resetSelection ? [] : selectedRows).map((row) => row.rdoCode))
          if (resetSelection) {
            setSelectedRows([])
            setAllRowsSelected(false)
          }
          setRows(response.body.items.map((item) => toRow(item, preserveAllSelected || selectedKeys.has(item.rdoCode))))
          setPageInfo({
            page: response.body.page.page,
            totalPages: response.body.page.totalPages,
            totalElements: response.body.page.totalElements,
            first: response.body.page.first,
            last: response.body.page.last
          })
          setIsLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setRows([])
          setIsLoading(false)
          setModalInfo({ title: 'Tìm kiếm thất bại', message: 'Đã có lỗi xảy ra. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => {
    lastSearchValues.current = values
    search(values, 0, true)
  })

  const handlePageChange = (page: number) => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, page)
    }
  }

  useEffect(() => {
    if (isRequireSearch) {
      handleSearch()
    }
  }, [isRequireSearch])

  const handleSelectAll = (selected: boolean) => {
    setRows((currentRows) => currentRows.map((row) => ({ ...row, checked: selected })))

    if (!selected) {
      setSelectedRows([])
      setAllRowsSelected(false)
      return
    }

    if (!lastSearchValues.current || !pageInfo || pageInfo.totalPages <= 1) {
      setSelectedRows(rows)
      setAllRowsSelected(true)
      return
    }

    const values = lastSearchValues.current
    setIsLoading(true)
    Promise.all(
      Array.from({ length: pageInfo.totalPages }, (_, page) =>
        pipe(
          PolicyDispatchingService.searchRdo({
            rdoCode: values.rdoCode?.trim() || undefined,
            fullName: values.fullName?.trim() || undefined,
            nonFileNet: 'false',
            page
          }),
          ZIO.runPromise({})
        )
      )
    )
      .then((responses) => {
        const allRows = responses.flatMap((response) => {
          const data = unwrapZioEither(response)
          return hasRdoBody(data) ? data.body.items.map((item) => toRow(item, true)) : []
        })
        setSelectedRows(allRows.length > 0 ? allRows : rows)
        setAllRowsSelected(true)
        setIsLoading(false)
      })
      .catch(() => {
        setSelectedRows(rows)
        setIsLoading(false)
        setModalInfo({
          title: 'Chọn tất cả',
          message: 'Không thể tải toàn bộ danh sách RDO. Đã chọn các mục ở trang hiện tại.'
        })
        setOpenModal(true)
      })
  }

  const handleDeactivate = () => {
    if (selectedRows.length === 0) {
      setModalInfo({
        title: 'Ngưng sử dụng',
        message: 'Vui lòng chọn ít nhất một RDO đang sử dụng để ngưng sử dụng.'
      })
      setOpenModal(true)
      return
    }
    const condition = selectedRows.every((row) => !row.inactive)
    if (!condition) {
      setModalInfo({
        title: 'Ngưng sử dụng',
        message: 'Chỉ có thể ngưng sử dụng đối với các RDO đang sử dụng.'
      })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.deactivateRdo(selectedRows.map((row) => row.rdoCode || '')),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (response.responseStatus.code !== 1 && response.responseStatus.code !== 200) {
            setModalInfo({ title: 'Ngưng sử dụng', message: 'Không thể ngừng sử dụng RDO.' })
            setOpenModal(true)
            return
          }

          setSelectedRows([])
          setAllRowsSelected(false)
          if (lastSearchValues.current && pageInfo) {
            search(lastSearchValues.current, 0, true)
          }
          setModalInfo({ title: 'Ngưng sử dụng', message: 'Ngưng sử dụng thành công.' })
          setOpenModal(true)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({ title: 'Ngưng sử dụng', message: 'Đã có lỗi xảy ra. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleViewSignature = (signatureUrl: string) => {
    pipe(
      ZIO.zipPar(AuthService.token, AuthService.getLoginType),
      ZIO.flatMap(([token, loginType]) =>
        ZIO.fromPromise(() =>
          fetch(signatureUrl, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Authen-Vendor': loginType
            }
          })
        )
      ),
      ZIO.flatMap((res) => {
        if (!res.ok) {
          return ZIO.fail(Throwable(`Fetch signature failed with status ${res.status}`))
        }
        const contentType = res.headers.get('content-type') || 'image/png'
        return pipe(
          ZIO.fromPromise(() => res.blob()),
          ZIO.map((blob) => new Blob([blob], { type: contentType }))
        )
      }),
      ZIO.map((fileBlob: Blob) => {
        const blobUrl = window.URL.createObjectURL(fileBlob)
        window.open(blobUrl, '_blank')
      }),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          window.open(signatureUrl, '_blank')
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const getStyle = (cell: string, value: any) => {
    if (cell === 'statusLabel') {
      const active = value === 'Đang dùng'
      return {
        alignSelf: 'flex-start',
        borderRadius: 999,
        paddingTop: 3,
        paddingRight: 10,
        paddingBottom: 3,
        paddingLeft: 10,
        fontSize: 12,
        fontWeight: '600',
        color: active ? '#16a34a' : '#6b7280',
        backgroundColor: active ? '#dcfce7' : '#f3f4f6'
      }
    }
    return {}
  }

  const renderCell = (cell: string, value: any, row: RdoRow) => {
    if (cell === 'signatureUrl') {
      return value ? (
        <button type="button" style={styles.linkButton} onClick={() => handleViewSignature(value)}>
          Xem
        </button>
      ) : (
        <span style={styles.mutedText}>—</span>
      )
    }
    if (cell === 'rdoCode') {
      return (
        <button
          type="button"
          style={styles.linkButton}
          onClick={() => navigation.navigate('PDCreateRdoScreen', { rdoItem: row })}
        >
          {value}
        </button>
      )
    }
    return undefined
  }

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <View style={styles.headerRow}>
        <View style={styles.title}>Danh sách RDO</View>
        <button
          type="button"
          style={allowCreate ? styles.primaryButton : styles.disabledActionButton}
          disabled={!allowCreate}
          onClick={() => navigation.navigate('PDCreateRdoScreen')}
        >
          <span style={styles.plusIcon}>+</span>
          Tạo RDO mới
        </button>
      </View>

      <View style={styles.searchCard}>
        <View style={styles.searchHint}>Vui lòng nhập ít nhất một trong các thông tin sau để tìm kiếm</View>
        <View style={styles.searchRow}>
          <View style={styles.searchInput}>
            <Controller
              name="rdoCode"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Mã RDO'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                />
              )}
            />
          </View>

          <View style={styles.searchInput}>
            <Controller
              name="fullName"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Họ và tên'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                />
              )}
            />
          </View>

          <button type="button" style={styles.searchButton} onClick={handleSearch} disabled={isLoading}>
            <span style={styles.mr_5}>
              <assets.SearchWhite />
            </span>
            Tìm
          </button>
        </View>
      </View>

      <View style={styles.actionRow}>
        <button
          type="button"
          style={
            !allowDeactivate
              ? { ...styles.disabledActionButton, width: 'fit-content' }
              : { ...styles.primaryButton, width: 'fit-content' }
          }
          disabled={!allowDeactivate}
          onClick={handleDeactivate}
        >
          <span style={styles.mr_5}>×</span>
          Ngưng sử dụng
        </button>
      </View>

      <View style={styles.tableCard}>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          hasCheckbox
          hasCheckAll
          getStyle={getStyle}
          renderCell={renderCell}
          onSelectionChange={(nextRows, nextSelectedRows) => {
            setRows(nextRows)
            setSelectedRows((currentSelectedRows) => {
              const currentPageKeys = new Set(rows.map((row) => row.rdoCode))
              const selectionsFromOtherPages = currentSelectedRows.filter((row) => !currentPageKeys.has(row.rdoCode))
              const mergedSelectedRows = [...selectionsFromOtherPages, ...nextSelectedRows]

              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && mergedSelectedRows.length === pageInfo.totalElements)
              )

              return mergedSelectedRows
            })
          }}
          onSelectAllChange={handleSelectAll}
          allRowsSelected={Boolean(allRowsSelected && pageInfo && pageInfo.totalElements > 0)}
          pagination={pageInfo ? { ...pageInfo, onPageChange: handlePageChange } : undefined}
        />
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '18px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600'
  },
  primaryButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    borderRadius: '12px',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  plusIcon: {
    fontSize: '18px',
    lineHeight: 1,
    marginRight: '8px'
  },
  searchCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    paddingTop: 18,
    paddingRight: 18,
    paddingBottom: 16,
    paddingLeft: 18,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
    marginBottom: '18px'
  },
  searchHint: {
    fontSize: '13px',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginBottom: '12px'
  },
  searchRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '14px'
  },
  searchInput: {
    width: '22%'
  },
  searchButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    borderRadius: '14px',
    paddingTop: 9,
    paddingRight: 18,
    paddingBottom: 9,
    paddingLeft: 18,
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '40px'
  },
  actionRow: {
    marginBottom: '18px'
  },
  disabledActionButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    backgroundColor: '#f8fafc',
    color: '#9ca3af',
    borderRadius: '12px',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'not-allowed'
  },
  tableCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
    padding: '20px'
  },
  linkButton: {
    background: 'none',
    borderWidth: 0,
    padding: 0,
    color: '#ED1B2E',
    fontWeight: 700,
    textDecoration: 'underline',
    width: 'fit-content'
  },
  mutedText: {
    color: '#cbd5e1',
    fontWeight: 600
  },
  mr_5: {
    marginRight: '5px'
  }
}
